import { useCallback, useEffect, useState } from "react"
import type { OpenCodeApi } from "../../lib/opencodeApi"
import type { FileNode } from "../../lib/opencodeTypes"
import { Empty, Spinner } from "../ui"

interface FileBrowserProps {
  api: OpenCodeApi
}

export function FileBrowser({ api }: FileBrowserProps) {
  const [roots, setRoots] = useState<FileNode[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  // Children are fetched per directory and kept, keyed by the directory's own
  // path. Collapsing is a UI state change, not a reason to throw the listing
  // away and fetch it again on the next click.
  const [children, setChildren] = useState<Map<string, FileNode[]>>(new Map())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [loadingDirs, setLoadingDirs] = useState<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false

    // No path: the agent lists the project root and tells us which directory
    // that was.
    api
      .listFiles()
      .then((entries) => {
        if (cancelled) return
        setRoots(entries)
        setError(null)
      })
      .catch((err) => {
        if (cancelled) return
        console.error("Failed to load files:", err)
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [api])

  const toggleDir = useCallback(
    async (path: string) => {
      const isOpen = expanded.has(path)

      setExpanded((prev) => {
        const next = new Set(prev)
        if (isOpen) next.delete(path)
        else next.add(path)
        return next
      })

      if (isOpen || children.has(path)) return

      setLoadingDirs((prev) => new Set(prev).add(path))
      try {
        // A child entry's `path` is already the full path relative to the
        // project root, so it is passed straight back with no joining.
        const entries = await api.listFiles(path)
        setChildren((prev) => new Map(prev).set(path, entries))
      } catch (err) {
        console.error(`Failed to list ${path}:`, err)
        // Cached as empty so the row stops spinning; expanding again refetches
        // only after a collapse, which is a reasonable retry gesture.
        setChildren((prev) => new Map(prev).set(path, []))
      } finally {
        setLoadingDirs((prev) => {
          const next = new Set(prev)
          next.delete(path)
          return next
        })
      }
    },
    [api, children, expanded],
  )

  const openFile = useCallback(
    async (path: string) => {
      setSelectedFile(path)
      setFileContent(null)
      setFileError(null)
      try {
        const { content } = await api.readFile(path)
        setFileContent(content)
      } catch (err) {
        console.error("Failed to load file:", err)
        setFileError(err instanceof Error ? err.message : String(err))
      }
    },
    [api],
  )

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border p-3">
        <h3 className="text-sm font-semibold text-foreground">Files</h3>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-64 shrink-0 overflow-y-auto border-r border-border">
          {error ? (
            <p className="p-3 text-xs text-danger">{error}</p>
          ) : (
            <FileTree
              entries={roots}
              childrenByPath={children}
              expanded={expanded}
              loadingDirs={loadingDirs}
              onToggleDir={toggleDir}
              onSelectFile={openFile}
              selectedFile={selectedFile}
            />
          )}
        </div>

        <div className="flex-1 overflow-auto">
          {selectedFile ? (
            <div className="p-4">
              <div className="mb-2 text-xs text-muted">{selectedFile}</div>
              {fileError ? (
                <p className="text-xs text-danger">{fileError}</p>
              ) : (
                <pre className="overflow-auto rounded bg-surface-muted p-3 text-xs">
                  <code>{fileContent ?? "Loading…"}</code>
                </pre>
              )}
            </div>
          ) : (
            <Empty title="No file selected" hint="Select a file from the tree to view its contents" />
          )}
        </div>
      </div>
    </div>
  )
}

interface FileTreeProps {
  entries: FileNode[]
  childrenByPath: Map<string, FileNode[]>
  expanded: Set<string>
  loadingDirs: Set<string>
  onToggleDir: (path: string) => void
  onSelectFile: (path: string) => void
  selectedFile: string | null
  depth?: number
}

function FileTree({ entries, depth = 0, ...rest }: FileTreeProps) {
  const sorted = [...entries].sort((a, b) => {
    const aDir = a.type === "directory"
    const bDir = b.type === "directory"
    if (aDir !== bDir) return aDir ? -1 : 1
    return a.name.localeCompare(b.name)
  })

  return (
    <div className="flex flex-col">
      {sorted.map((entry) => (
        <FileTreeItem key={entry.path} entry={entry} depth={depth} {...rest} />
      ))}
    </div>
  )
}

interface FileTreeItemProps extends Omit<FileTreeProps, "entries"> {
  entry: FileNode
  depth: number
}

function FileTreeItem({ entry, depth, ...rest }: FileTreeItemProps) {
  const { childrenByPath, expanded, loadingDirs, onToggleDir, onSelectFile, selectedFile } = rest
  const paddingLeft = depth * 12 + 8

  if (entry.type === "directory") {
    const isExpanded = expanded.has(entry.path)
    const isLoading = loadingDirs.has(entry.path)
    const kids = childrenByPath.get(entry.path)

    return (
      <div>
        <button
          onClick={() => onToggleDir(entry.path)}
          className="flex w-full items-center gap-1 px-2 py-1 text-left text-xs hover:bg-surface-muted"
          style={{ paddingLeft }}
        >
          <span className="text-muted">{isExpanded ? "▼" : "▶"}</span>
          <span className="text-foreground">📁 {entry.name}</span>
          {isLoading && <span className="text-faint">…</span>}
        </button>
        {isExpanded && kids && (
          <FileTree entries={kids} depth={depth + 1} {...rest} />
        )}
      </div>
    )
  }

  const isSelected = selectedFile === entry.path

  return (
    <button
      onClick={() => onSelectFile(entry.path)}
      className={`flex w-full items-center gap-1 px-2 py-1 text-left text-xs hover:bg-surface-muted ${
        isSelected ? "bg-accent-soft text-accent" : "text-foreground"
      }`}
      style={{ paddingLeft: paddingLeft + 16 }}
    >
      <span>📄 {entry.name}</span>
    </button>
  )
}
